/**
 * JSON import/export shape for the seat map editor (apps/dashboard/src/features/seatmap).
 *
 * `packages/core/src/seatmaps/` does not exist yet at the time this was written (only
 * packages/core/README.md). There is therefore no `importSeatMapFromJson` to read the expected
 * shape from. This shape is this agent's own reasonable design, chosen to map 1:1 onto the
 * `seat_maps` / `seat_sections` / `seats` tables in packages/db/src/schema/catalog.ts so the
 * Ticketing-core agent's importer can stay a thin mapper over it.
 *
 * FLAG FOR THE TICKETING-CORE AGENT: when `importSeatMapFromJson` is built, confirm it accepts
 * exactly this document shape (or tell this agent what to change). Key choices made here that a
 * different importer design might reasonably disagree with:
 *  - `seats[].row` / `seats[].number` are strings (matches `rowLabel`/`seatNumber` columns, which
 *    are text - row "AA" or seat "12B" are valid, not just integers).
 *  - `x`/`y` are editor canvas pixel coordinates, nullable (GA-only sections have no seats).
 *  - `priceZone` is a free-text label the ticket-type editor can reference, not a foreign key to
 *    a priced entity - price is still set on `ticket_types`, zones are just a grouping label here.
 *  - version is included so the importer can reject/upgrade older exports later.
 */

export interface SeatMapImportSeat {
  id?: string;
  row: string;
  number: string;
  x: number | null;
  y: number | null;
  isAccessible: boolean;
  status?: 'available' | 'sold' | 'disabled';
}

export interface SeatMapImportSection {
  id?: string;
  name: string;
  priceZone: string;
  isAccessible: boolean;
  seats: SeatMapImportSeat[];
}

export interface SeatMapImportDocument {
  version: 1;
  name: string;
  sections: SeatMapImportSection[];
}

export class SeatMapImportError extends Error {}

export function parseSeatMapImport(raw: string): SeatMapImportDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    throw new SeatMapImportError(
      `That file is not valid JSON: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  return validateSeatMapImport(parsed);
}

export function validateSeatMapImport(value: unknown): SeatMapImportDocument {
  if (typeof value !== 'object' || value === null) {
    throw new SeatMapImportError('Seat map JSON must be an object at the top level.');
  }
  const doc = value as Record<string, unknown>;
  if (doc.version !== 1) {
    throw new SeatMapImportError(
      `Unsupported seat map version "${String(doc.version)}" - this editor only reads version 1.`,
    );
  }
  if (typeof doc.name !== 'string' || doc.name.trim() === '') {
    throw new SeatMapImportError('Seat map JSON is missing a non-empty "name" field.');
  }
  if (!Array.isArray(doc.sections)) {
    throw new SeatMapImportError('Seat map JSON is missing a "sections" array.');
  }

  const sections: SeatMapImportSection[] = doc.sections.map((rawSection, sIdx) => {
    if (typeof rawSection !== 'object' || rawSection === null) {
      throw new SeatMapImportError(`sections[${sIdx}] must be an object.`);
    }
    const section = rawSection as Record<string, unknown>;
    if (typeof section.name !== 'string' || section.name.trim() === '') {
      throw new SeatMapImportError(`sections[${sIdx}] is missing a non-empty "name".`);
    }
    if (typeof section.priceZone !== 'string' || section.priceZone.trim() === '') {
      throw new SeatMapImportError(`sections[${sIdx}] ("${section.name}") is missing "priceZone".`);
    }
    if (!Array.isArray(section.seats)) {
      throw new SeatMapImportError(
        `sections[${sIdx}] ("${section.name}") is missing a "seats" array.`,
      );
    }
    const seats: SeatMapImportSeat[] = section.seats.map((rawSeat, seatIdx) => {
      if (typeof rawSeat !== 'object' || rawSeat === null) {
        throw new SeatMapImportError(`sections[${sIdx}].seats[${seatIdx}] must be an object.`);
      }
      const seat = rawSeat as Record<string, unknown>;
      if (typeof seat.row !== 'string' || typeof seat.number !== 'string') {
        throw new SeatMapImportError(
          `sections[${sIdx}].seats[${seatIdx}] must have string "row" and "number" fields.`,
        );
      }
      return {
        id: typeof seat.id === 'string' ? seat.id : undefined,
        row: seat.row,
        number: seat.number,
        x: typeof seat.x === 'number' ? seat.x : null,
        y: typeof seat.y === 'number' ? seat.y : null,
        isAccessible: Boolean(seat.isAccessible),
        status:
          seat.status === 'available' || seat.status === 'sold' || seat.status === 'disabled'
            ? seat.status
            : 'available',
      };
    });
    return {
      id: typeof section.id === 'string' ? section.id : undefined,
      name: section.name,
      priceZone: section.priceZone,
      isAccessible: Boolean(section.isAccessible),
      seats,
    };
  });

  return { version: 1, name: doc.name, sections };
}

export function exportSeatMapToJson(doc: SeatMapImportDocument): string {
  return JSON.stringify(doc, null, 2);
}
